// The stats bar's automatic numbers. One definition, read by the
// editor and by every template's stats block: two templates reading
// the same config have to answer the same numbers, and a source the
// editor offers that one template does not draw would be a dash on
// somebody's home page.
//
// The "this year" figures come from the annual report (SiteData::
// yearStats), January 1 to today on the organization's calendar.
//
// No imports, so the harness can run it in node.

const year = (s) => (s.year ? ` in ${s.year}` : ' this year');
const n = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('en-US'));
// The label agrees with the number beside it: "1 Mile", never "1 Miles".
const per = (v, one, many) => (Number(v) === 1 ? one : many);

export const STAT_SOURCES = [
    { key: 'trail_count', group: 'Your trails', label: 'Total trails', value: (s) => n(s.trail_count), name: (s) => per(s.trail_count, 'Trail', 'Trails') },
    {
        key: 'distance', group: 'Your trails', label: 'Total distance',
        value: (s) => n(s.unit_system === 'imperial' ? s.distance_miles : s.distance_km),
        name: (s) => (s.unit_system === 'imperial'
            ? per(s.distance_miles, 'Mile', 'Miles')
            : per(s.distance_km, 'Kilometer', 'Kilometers')),
    },
    { key: 'network_count', group: 'Your trails', label: 'Networks', value: (s) => n(s.network_count), name: (s) => per(s.network_count, 'Network', 'Networks') },
    { key: 'structure_count', group: 'Your trails', label: 'Bridges and structures', value: (s) => n(s.structure_count), name: (s) => per(s.structure_count, 'Bridge or structure', 'Bridges and structures') },

    { key: 'volunteer_hours_year', group: 'This year', label: 'Volunteer hours', value: (s) => n(s.volunteer_hours_year), name: (s) => `${per(s.volunteer_hours_year, 'Volunteer hour', 'Volunteer hours')}${year(s)}` },
    { key: 'volunteers_year', group: 'This year', label: 'Volunteers', value: (s) => n(s.volunteers_year), name: (s) => `${per(s.volunteers_year, 'Volunteer', 'Volunteers')}${year(s)}` },
    { key: 'tasks_completed_year', group: 'This year', label: 'Trail tasks completed', value: (s) => n(s.tasks_completed_year), name: (s) => `${per(s.tasks_completed_year, 'Trail task completed', 'Trail tasks completed')}${year(s)}` },
    { key: 'events_year', group: 'This year', label: 'Events held', value: (s) => n(s.events_year), name: (s) => `${per(s.events_year, 'Event', 'Events')}${year(s)}` },
    { key: 'attendees_year', group: 'This year', label: 'Event attendees', value: (s) => n(s.attendees_year), name: (s) => `${per(s.attendees_year, 'Event attendee', 'Event attendees')}${year(s)}` },
    { key: 'trail_visits_year', group: 'This year', label: 'Trail visits counted', value: (s) => n(s.trail_visits_year), name: (s) => `${per(s.trail_visits_year, 'Trail visit', 'Trail visits')}${year(s)}` },
    {
        key: 'open_pct_year', group: 'This year', label: 'Days the trails were open',
        value: (s) => (s.open_pct_year === null || s.open_pct_year === undefined ? '—' : `${s.open_pct_year}%`),
        name: (s) => `Of days open${year(s)}`,
    },
];

export const STAT_GROUPS = ['Your trails', 'This year'];

export const DEFAULT_STAT_SLOTS = () => [
    { source: 'trail_count' },
    { source: 'distance' },
    { source: 'network_count' },
    { source: 'custom', value: 'YEAR-ROUND', label: 'Open' },
];

export function statSource(key) {
    return STAT_SOURCES.find((s) => s.key === key) || null;
}

/** One slot → { value, label }, as every stats block draws it. */
export function resolveStat(slot, stats = {}) {
    const src = slot.source === 'custom' ? null : statSource(slot.source || 'trail_count');
    const value = slot.source === 'custom' ? (slot.value || '—') : (src ? src.value(stats) : '—');
    const fallback = src ? src.name(stats) : '';
    const label = slot.label && slot.label.trim() !== '' ? slot.label : fallback;
    return { value: value === '—' ? value : `${value}${slot.plus ? '+' : ''}`, label };
}

/** The label a slot shows when its own is blank, for the editor. */
export function defaultStatLabel(slot, stats = {}) {
    const src = statSource(slot.source);
    return src ? src.name(stats) : '';
}

/**
 * The cells a stats bar draws. In the editor, every slot, so a manager
 * sees the whole bar they are building. For a visitor, a number from the
 * organization's data that is zero or missing is left out — "0 Trails"
 * and a dash are a stat with nothing to say — and a bar left with no
 * number from the data at all, on the default slots nobody chose, is
 * not drawn: what remains is the starter "YEAR-ROUND Open" cell, which
 * is filler rather than a fact (Mus, 2026-10-11). A custom stat the
 * manager wrote is theirs and always stays.
 */
export function visibleStats(config, stats = {}, editing = false) {
    const configured = Array.isArray(config?.items) && config.items.length > 0;
    const slots = configured ? config.items : DEFAULT_STAT_SLOTS();
    const cells = slots.map((slot) => ({ slot, ...resolveStat(slot, stats) }));
    if (editing) return cells;
    const empty = (c) => c.slot.source !== 'custom' && (c.value === '—' || Number(String(c.value).replace(/[^0-9.]/g, '')) === 0);
    const kept = cells.filter((c) => !empty(c));
    if (!configured && !kept.some((c) => c.slot.source !== 'custom')) return [];
    return kept;
}
